import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Lock, LogOut, Users, Crown, Sparkles, Gift, Download, Loader2, RefreshCw, Dumbbell, Mail, HeartPulse, Wallet, FileText, Trash2, MapPin, UserPlus, TrendingUp, IdCard, ShieldAlert, MessageSquareWarning, CreditCard, Calendar, ChevronRight, Shield, MailX, BookOpen, ActivitySquare } from 'lucide-react';
import { CoachPanel } from './admin/CoachPanel';
import { AdminStandardProgramsPanel } from './admin/AdminStandardProgramsPanel';
import { AdminClientsPanel } from './admin/AdminClientsPanel';
import { AdminPaymentsPanel } from './admin/AdminPaymentsPanel';
import { AdminBillingPanel } from './admin/AdminBillingPanel';
import { AdminLeadsQualifPanel } from './admin/AdminLeadsQualifPanel';
import { AdminPresentielPanel } from './admin/AdminPresentielPanel';
import { ClientAccessPanel } from './admin/ClientAccessPanel';
import { AdminMRRPanel } from './admin/AdminMRRPanel';
import { AdminHealthPanel } from './admin/AdminHealthPanel';
import { AdminSecurityPanel } from './admin/AdminSecurityPanel';
import { AdminNotificationsPanel } from './admin/AdminNotificationsPanel';
import { AdminRecipesPanel } from './admin/AdminRecipesPanel';
import { AdminPromoCodesPanel } from './admin/AdminPromoCodesPanel';
import { AdminPushOptIn } from './admin/AdminPushOptIn';
import { AdminVideoReportsPanel } from './admin/AdminVideoReportsPanel';
import { AdminSiteSettingsPanel } from './admin/AdminSiteSettingsPanel';
import { ThemeToggle } from '../components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatBox = ({ icon: Icon, label, value, color }) => (
  <div className="bg-zinc-900 border border-white/10 rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <Icon className="w-6 h-6 mb-2" style={{ color }} />
    <div className="text-3xl font-extrabold text-white">{value}</div>
    <div className="text-sm text-zinc-400 mt-1">{label}</div>
  </div>
);

const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [twoFaStep, setTwoFaStep] = useState(null); // { pre_auth_token }
  const [code, setCode] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Important : permet l'envoi du cookie HttpOnly
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Connexion échouée');
        return;
      }
      if (data.require_2fa) {
        setTwoFaStep({ pre_auth_token: data.pre_auth_token });
        return;
      }
      // Le token CSRF est retourné par le backend, on le stocke en mémoire via callback
      onSuccess(data.csrf_token);
    } catch {
      setError('Erreur réseau. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pre_auth_token: twoFaStep.pre_auth_token, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Code invalide');
        return;
      }
      onSuccess(data.csrf_token);
    } catch {
      setError('Erreur réseau. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  if (twoFaStep) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <form onSubmit={submit2fa} className="w-full max-w-sm bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8" data-testid="admin-2fa-form">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center mb-3">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Code de vérification</h1>
            <p className="text-zinc-500 text-sm mt-1 text-center">Saisissez le code à 6 chiffres de votre application d'authentification (ou un code de secours)</p>
          </div>
          <input
            type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required autoFocus
            maxLength={10}
            className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white text-center text-xl tracking-widest placeholder-zinc-600 focus:border-primary focus:outline-none mb-4"
            data-testid="admin-2fa-code-input"
          />
          {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-2fa-error">{error}</p>}
          <button
            type="submit" disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-transform hover:scale-[1.02] disabled:opacity-50"
            data-testid="admin-2fa-submit-btn"
          >
            {loading ? 'Vérification...' : 'Valider'}
          </button>
          <button type="button" onClick={() => { setTwoFaStep(null); setCode(''); setError(''); }} className="w-full mt-3 text-zinc-500 hover:text-white text-xs" data-testid="admin-2fa-back-btn">
            ← Retour
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8" data-testid="admin-login-form">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center mb-3">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Body-Up</h1>
          <p className="text-zinc-500 text-sm mt-1">Tableau de bord des leads</p>
        </div>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none mb-3"
          data-testid="admin-email-input"
        />
        <input
          type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" required
          className="w-full bg-zinc-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-zinc-600 focus:border-primary focus:outline-none mb-4"
          data-testid="admin-password-input"
        />
        {error && <p className="text-red-400 text-sm mb-4" data-testid="admin-login-error">{error}</p>}
        <button
          type="submit" disabled={loading}
          className="w-full py-3 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl transition-transform hover:scale-[1.02] disabled:opacity-50"
          data-testid="admin-login-btn"
        >
          {loading ? 'Connexion...' : 'Se connecter'}
        </button>
        <p className="mt-4 text-center text-xs text-zinc-600" data-testid="client-hint-link">
          Vous êtes client ?{' '}
          <a href="/espace-client" className="text-zinc-500 hover:text-primary underline underline-offset-2">Espace client</a>
        </p>
      </form>
    </div>
  );
};

const AdminDashboard = ({ onLogout, csrfToken }) => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [promos, setPromos] = useState([]);
  const [funnelLeads, setFunnelLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('leads');
  const [coachInitialEmail, setCoachInitialEmail] = useState(null);
  const [clientsInitialEmail, setClientsInitialEmail] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [followupMsg, setFollowupMsg] = useState('');
  const [followupLoading, setFollowupLoading] = useState(false);

  const goToClients = (email) => { if (email) setClientsInitialEmail(email); setTab('clients'); };

  // Helper pour les requêtes GET (pas de CSRF nécessaire)
  const fetchWithCredentials = (url) => fetch(url, { credentials: 'include' });

  // Helper pour les requêtes POST/PUT/DELETE (avec CSRF)
  const fetchWithCsrf = (url, options = {}) => {
    return fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        'X-CSRF-Token': csrfToken,
      },
    });
  };

  const runFollowups = async () => {
    setFollowupLoading(true);
    setFollowupMsg('');
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/run-followups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.status === 401) { onLogout(); return; }
      const data = await res.json();
      setFollowupMsg(`${data.sent} relance(s) envoyée(s) sur ${data.candidates} lead(s) éligible(s).`);
    } catch {
      setFollowupMsg('Erreur lors de l\'envoi des relances.');
    } finally {
      setFollowupLoading(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [s, l, p] = await Promise.all([
        fetchWithCredentials(`${BACKEND_URL}/api/admin/stats`),
        fetchWithCredentials(`${BACKEND_URL}/api/admin/leads`),
        fetchWithCredentials(`${BACKEND_URL}/api/admin/promo-redemptions`),
      ]);
      if (s.status === 401 || l.status === 401) { onLogout(); return; }
      setStats(await s.json());
      setLeads((await l.json()).leads || []);
      setPromos((await p.json()).redemptions || []);
      const f = await fetchWithCredentials(`${BACKEND_URL}/api/admin/funnel-leads`);
      setFunnelLeads(f.ok ? ((await f.json()).leads || []) : []);
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadAlerts = useCallback(() => {
    fetchWithCredentials(`${BACKEND_URL}/api/admin/alerts-summary`)
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && setAlerts(d)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const downloadCsv = async () => {
    const endpoint = tab === 'funnel' ? 'funnel-leads.csv' : 'leads.csv';
    const filename = tab === 'funnel' ? 'body-up-funnel-leads.csv' : 'body-up-leads.csv';
    const res = await fetchWithCredentials(`${BACKEND_URL}/api/admin/${endpoint}`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
  };

  const deleteItem = async (kind, id) => {
    if (!window.confirm('Supprimer définitivement cet élément ? Cette action est irréversible.')) return;
    const endpoint = kind === 'lead' ? `leads/${encodeURIComponent(id)}`
      : kind === 'promo' ? `promo-redemptions/${encodeURIComponent(id)}`
      : `funnel-leads/${encodeURIComponent(id)}`;
    try {
      const res = await fetchWithCsrf(`${BACKEND_URL}/api/admin/${endpoint}`, { method: 'DELETE' });
      if (res.status === 401) { onLogout(); return; }
      if (!res.ok) return;
      if (kind === 'lead') setLeads((ls) => ls.filter((l) => l.calculation_id !== id));
      if (kind === 'promo') setPromos((ps) => ps.filter((p) => p.redemption_id !== id));
      if (kind === 'funnel') setFunnelLeads((fs) => fs.filter((f) => f.email !== id));
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-black px-4 sm:px-8 py-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-white">Tableau de bord</h1>
            <p className="text-zinc-500 text-sm">Leads du calculateur de calories</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <ThemeToggle />
            <AdminPushOptIn csrfToken={csrfToken} />
            <button onClick={() => { setLoading(true); loadData(); }} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-sm transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw className="w-4 h-4" /> Actualiser
            </button>
            <button onClick={downloadCsv} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl text-sm font-semibold transition-transform hover:scale-105" data-testid="admin-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={runFollowups} disabled={followupLoading} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-400 to-primary text-black rounded-xl text-sm font-semibold transition-transform hover:scale-105 disabled:opacity-50" data-testid="admin-followup-btn">
              {followupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Relancer (-20%)
            </button>
            <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-sm transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>

        {followupMsg && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm" data-testid="admin-followup-msg">
            {followupMsg}
          </div>
        )}

        {/* Bannière d'alertes critiques — visible dès la connexion, sans naviguer entre onglets */}
        {alerts && alerts.total > 0 && (
          <div className="mb-6 rounded-2xl border border-red-500/25 bg-red-500/[0.04] p-4" data-testid="admin-alerts-banner">
            <h3 className="flex items-center gap-2 text-red-300 font-bold text-sm mb-3">
              <ShieldAlert className="w-4 h-4" /> {alerts.total} alerte{alerts.total > 1 ? 's' : ''} à traiter
            </h3>
            <div className="flex flex-wrap gap-2">
              {alerts.unread_messages > 0 && (
                <button onClick={() => setTab('coaching')} className="inline-flex items-center gap-2 px-3 py-2 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/25 rounded-xl text-violet-300 text-xs font-semibold transition-colors" data-testid="alert-chip-messages">
                  <MessageSquareWarning className="w-3.5 h-3.5" /> {alerts.unread_messages} message{alerts.unread_messages > 1 ? 's' : ''} non lu{alerts.unread_messages > 1 ? 's' : ''} <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.failed_payments > 0 && (
                <button onClick={() => setTab('billing')} className="inline-flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-xl text-red-300 text-xs font-semibold transition-colors" data-testid="alert-chip-failed-payments">
                  <CreditCard className="w-3.5 h-3.5" /> {alerts.failed_payments} paiement{alerts.failed_payments > 1 ? 's' : ''} récurrent{alerts.failed_payments > 1 ? 's' : ''} en échec <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.overdue_invoices > 0 && (
                <button onClick={() => setTab('billing')} className="inline-flex items-center gap-2 px-3 py-2 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/25 rounded-xl text-orange-300 text-xs font-semibold transition-colors" data-testid="alert-chip-overdue-invoices">
                  <FileText className="w-3.5 h-3.5" /> {alerts.overdue_invoices} facture{alerts.overdue_invoices > 1 ? 's' : ''} en retard (7j+) <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.expiring_soon > 0 && (
                <button onClick={() => setTab('mrr')} className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/25 rounded-xl text-yellow-300 text-xs font-semibold transition-colors" data-testid="alert-chip-expiring">
                  <Calendar className="w-3.5 h-3.5" /> {alerts.expiring_soon} abonnement{alerts.expiring_soon > 1 ? 's' : ''} à échéance (7j) <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.presentiel_pending > 0 && (
                <button onClick={() => setTab('presentiel')} className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/25 rounded-xl text-blue-300 text-xs font-semibold transition-colors" data-testid="alert-chip-presentiel">
                  <MapPin className="w-3.5 h-3.5" /> {alerts.presentiel_pending} demande{alerts.presentiel_pending > 1 ? 's' : ''} présentiel <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.access_email_failures > 0 && (
                <button onClick={() => setTab('clients')} className="inline-flex items-center gap-2 px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/25 rounded-xl text-rose-300 text-xs font-semibold transition-colors" data-testid="alert-chip-email-failures">
                  <MailX className="w-3.5 h-3.5" /> {alerts.access_email_failures} email{alerts.access_email_failures > 1 ? 's' : ''} critique{alerts.access_email_failures > 1 ? 's' : ''} non envoyé{alerts.access_email_failures > 1 ? 's' : ''} <ChevronRight className="w-3 h-3" />
                </button>
              )}
              {alerts.low_calorie_overrides > 0 && (
                <button onClick={() => setTab('notifications')} className="inline-flex items-center gap-2 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/25 rounded-xl text-amber-300 text-xs font-semibold transition-colors" data-testid="alert-chip-low-calorie">
                  <ShieldAlert className="w-3.5 h-3.5" /> {alerts.low_calorie_overrides} objectif{alerts.low_calorie_overrides > 1 ? 's' : ''} calorique{alerts.low_calorie_overrides > 1 ? 's' : ''} bas <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-10 h-10 text-primary animate-spin" /></div>
        ) : (
          <>
            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <StatBox icon={Users} label="Leads totaux" value={stats.total_leads} color="#E91E63" />
                <StatBox icon={Crown} label="Achats Pro" value={stats.paid_pro} color="#EC4899" />
                <StatBox icon={Sparkles} label="Plans alimentaires" value={stats.paid_meal_plan} color="#FBBF24" />
                <StatBox icon={Gift} label="Codes promo" value={stats.promo_used} color="#10B981" />
                <StatBox icon={Dumbbell} label="Paiements Stripe" value={stats.paid_transactions} color="#8B5CF6" />
                {stats.presentiel_pending > 0 && (
                  <StatBox icon={MapPin} label="Présentiel en attente" value={stats.presentiel_pending} color="#F59E0B" />
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <button onClick={() => setTab('leads')} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'leads' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-leads">Leads ({leads.length})</button>
              <button onClick={() => setTab('promos')} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'promos' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-promos">Codes promo</button>
              <button onClick={() => setTab('funnel')} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'funnel' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-funnel">Leads tunnel ({funnelLeads.length})</button>
              <button onClick={() => setTab('access')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'access' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-access"><UserPlus className="w-4 h-4" /> Accès Client</button>
              <button onClick={() => setTab('clients')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'clients' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-clients"><IdCard className="w-4 h-4" /> Clients</button>
              <button onClick={() => setTab('coaching')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'coaching' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-coaching">
                <HeartPulse className="w-4 h-4" /> Coaching{alerts?.unread_messages > 0 ? ` (${alerts.unread_messages})` : ''}
              </button>
              <button onClick={() => setTab('presentiel')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'presentiel' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-presentiel">
                <MapPin className="w-4 h-4" /> Présentiel{stats?.presentiel_pending > 0 ? ` (${stats.presentiel_pending})` : ''}
              </button>
              <button onClick={() => setTab('payments')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'payments' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-payments"><Wallet className="w-4 h-4" /> Paiements</button>
              <button onClick={() => setTab('billing')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'billing' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-billing"><FileText className="w-4 h-4" /> Facturation</button>
              <button onClick={() => setTab('mrr')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'mrr' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-mrr"><TrendingUp className="w-4 h-4" /> Revenus récurrents</button>
              <button onClick={() => setTab('health')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'health' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-health"><ActivitySquare className="w-4 h-4" /> Santé de l'app</button>
              <button onClick={() => setTab('qualification')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'qualification' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-qualification"><Sparkles className="w-4 h-4" /> Qualification</button>
              <button onClick={() => setTab('security')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'security' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-security"><Shield className="w-4 h-4" /> Sécurité</button>
              <button onClick={() => setTab('notifications')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'notifications' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-notifications">
                <ShieldAlert className="w-4 h-4" /> Alertes{alerts?.low_calorie_overrides > 0 ? ` (${alerts.low_calorie_overrides})` : ''}
              </button>
              <button onClick={() => setTab('recettes')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'recettes' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-recettes">
                <Mail className="w-4 h-4" /> Recettes
              </button>
              <button onClick={() => setTab('reports')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'reports' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-reports">
                <MessageSquareWarning className="w-4 h-4" /> Signalements{alerts?.open_video_reports > 0 ? ` (${alerts.open_video_reports})` : ''}
              </button>
              <button onClick={() => setTab('standard-programs')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'standard-programs' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-standard-programs">
                <BookOpen className="w-4 h-4" /> Programmes Standardisés
              </button>
              <button onClick={() => setTab('settings')} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'settings' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="admin-tab-settings">
                Réglages
              </button>
            </div>

            {/* Content */}
            {tab === 'access' ? (
              <ClientAccessPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} onGoToClients={goToClients} />
            ) : tab === 'clients' ? (
              <AdminClientsPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} onGoToCoaching={(email) => { setCoachInitialEmail(email); setTab('coaching'); }} initialEmail={clientsInitialEmail} onInitialEmailConsumed={() => setClientsInitialEmail(null)} />
            ) : tab === 'coaching' ? (
              <CoachPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} initialEmail={coachInitialEmail} onInitialEmailConsumed={() => setCoachInitialEmail(null)} />
            ) : tab === 'presentiel' ? (
              <AdminPresentielPanel csrfToken={csrfToken} fmtDate={fmtDate} />
            ) : tab === 'payments' ? (
              <AdminPaymentsPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} />
            ) : tab === 'billing' ? (
              <AdminBillingPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} onGoToClients={goToClients} />
            ) : tab === 'mrr' ? (
              <AdminMRRPanel onLogout={onLogout} csrfToken={csrfToken} />
            ) : tab === 'health' ? (
              <AdminHealthPanel onLogout={onLogout} />
            ) : tab === 'qualification' ? (
              <AdminLeadsQualifPanel csrfToken={csrfToken} fmtDate={fmtDate} />
            ) : tab === 'security' ? (
              <AdminSecurityPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} />
            ) : tab === 'notifications' ? (
              <AdminNotificationsPanel csrfToken={csrfToken} onLogout={onLogout} fmtDate={fmtDate} onGoToCoaching={(email) => { setCoachInitialEmail(email); setTab('coaching'); }} onChanged={loadAlerts} />
            ) : tab === 'recettes' ? (
              <AdminRecipesPanel csrfToken={csrfToken} onLogout={onLogout} />
            ) : tab === 'promos' ? (
              <AdminPromoCodesPanel csrfToken={csrfToken} />
            ) : tab === 'reports' ? (
              <AdminVideoReportsPanel csrfToken={csrfToken} onChanged={loadAlerts} />
            ) : tab === 'settings' ? (
              <AdminSiteSettingsPanel csrfToken={csrfToken} />
            ) : tab === 'standard-programs' ? (
              <AdminStandardProgramsPanel csrfToken={csrfToken} />
            ) : (
              <div className="bg-zinc-900 border border-white/10 rounded-2xl overflow-hidden overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-table">
                  <thead className="bg-zinc-800/70 text-zinc-400">
                    <tr>
                      {tab === 'leads' && ['Date', 'Email', 'Genre', 'Âge', 'Objectif', 'Poids', 'Cible', 'Morphotype', 'kcal', 'Pro payé', 'Plan payé', ''].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                      {tab === 'funnel' && ['Date', 'Email', 'Source', 'Objectif', ''].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {tab === 'leads' && leads.length === 0 && <tr><td colSpan={12} className="px-4 py-8 text-center text-zinc-500">Aucun lead</td></tr>}
                    {tab === 'leads' && leads.map((l, i) => (
                      <tr key={i} className="border-t border-white/5 text-zinc-300">
                        <td className="px-4 py-3 whitespace-nowrap text-zinc-500">{fmtDate(l.created_at)}</td>
                        <td className="px-4 py-3 text-white font-medium">{l.email}</td>
                        <td className="px-4 py-3">{l.gender === 'male' ? '♂' : '♀'}</td>
                        <td className="px-4 py-3">{l.age || '-'}</td>
                        <td className="px-4 py-3">{l.goal || '-'}</td>
                        <td className="px-4 py-3">{l.weight ? `${l.weight} kg` : '-'}</td>
                        <td className="px-4 py-3">{l.target_weight ? `${l.target_weight} kg` : '-'}</td>
                        <td className="px-4 py-3 text-primary font-semibold">{l.morphotype || '-'}</td>
                        <td className="px-4 py-3 text-white font-bold">{l.target_calories || '-'}</td>
                        <td className="px-4 py-3">{l.paid_pro ? '✅' : '—'}</td>
                        <td className="px-4 py-3">{l.paid_meal_plan ? '✅' : '—'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteItem('lead', l.calculation_id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-lead-${l.calculation_id}`} title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}

                    {tab === 'funnel' && funnelLeads.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500">Aucun lead tunnel</td></tr>}
                    {tab === 'funnel' && funnelLeads.map((f, i) => (
                      <tr key={i} className="border-t border-white/5 text-zinc-300">
                        <td className="px-4 py-3 whitespace-nowrap text-zinc-500">{fmtDate(f.created_at)}</td>
                        <td className="px-4 py-3 text-white font-medium">{f.email}</td>
                        <td className="px-4 py-3 text-primary">{f.source || '-'}</td>
                        <td className="px-4 py-3">{f.goal || '-'}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteItem('funnel', f.email)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-funnel-${f.email}`} title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [csrfToken, setCsrfToken] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const sessionCheckStarted = useRef(false);

  useEffect(() => {
    if (sessionCheckStarted.current) return;
    sessionCheckStarted.current = true;
    fetch(`${BACKEND_URL}/api/admin/me`, { credentials: 'include' })
      .then((r) => {
        if (r.status === 401) return null;
        return r.ok ? r.json() : Promise.reject(r);
      })
      .then((d) => { if (d) { setCsrfToken(d.csrf_token); setIsLoggedIn(true); } })
      .catch((e) => console.error('Session check error:', e))
      .finally(() => setCheckingSession(false));
  }, []);

  const handleLogin = (token) => {
    setCsrfToken(token);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (e) {
      console.error('Logout error:', e);
    }
    setCsrfToken(null);
    setIsLoggedIn(false);
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="admin-session-check-loader">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return isLoggedIn ? (
    <AdminDashboard onLogout={handleLogout} csrfToken={csrfToken} />
  ) : (
    <AdminLogin onSuccess={handleLogin} />
  );
}
